// 신규 폰트 제보 폼의 텍스트 입력(폰트명/제작사명) 자동완성.
//
// - 입력하면 urlValue 로 검색해 결과를 입력칸 하단 드롭다운으로 보여준다.
// - 결과를 클릭하면 그 값으로 입력칸을 채운다.
// - 결과가 없고 confirmTextValue 가 있으면 "이 이름으로 제보하시겠어요?" 안내를 보여준다.
//   (클릭은 단순 확인일 뿐, 굳이 클릭하지 않고 포커스만 빠져도 입력한 텍스트가 그대로 제출된다.)
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";

export default class extends Controller {
  static targets = ["input", "results"];
  static values = {
    url: String,
    // "{kw}" 자리에 입력 키워드가 들어간다. 비어 있으면 빈 결과 안내를 띄우지 않는다.
    confirmText: { type: String, default: "" },
  };

  initialize() {
    this.searchTimeout = null;
    this.lastKeyword = null;
    this.activeIndex = -1;
  }

  connect() {
    this.onClickOutside = this.onClickOutside.bind(this);
    document.addEventListener("click", this.onClickOutside);
    this.inputTarget.addEventListener("keydown", this.onKeydown);
  }

  disconnect() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    document.removeEventListener("click", this.onClickOutside);
    this.inputTarget.removeEventListener("keydown", this.onKeydown);
  }

  onType = () => {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.resetActiveItem();

    const keyword = this.inputTarget.value.trim();
    if (keyword === "") {
      this.lastKeyword = null;
      this.hide();
      return;
    }

    this.searchTimeout = setTimeout(() => this.search(keyword), 350);
  };

  onFocus = () => {
    const keyword = this.inputTarget.value.trim();
    if (keyword !== "") this.search(keyword);
  };

  search = (keyword) => {
    this.lastKeyword = keyword;
    api
      .get(this.urlValue, { q: keyword })
      .then((res) => {
        // 응답이 오는 사이 입력이 더 바뀌었으면 무시한다.
        if (this.inputTarget.value.trim() !== keyword) return;
        const results = (res.data && res.data.results) || [];
        this.render(results, keyword);
      })
      .catch(() => this.hide());
  };

  render = (results, keyword) => {
    this.resetActiveItem();
    const items = results.map((result) => this.resultTemplate(result));

    if (results.length === 0 && this.confirmTextValue) {
      items.push(this.confirmTemplate(keyword));
    }

    if (items.length === 0) {
      this.hide();
      return;
    }

    this.resultsTarget.innerHTML = items.join("\n");
    this.show();
  };

  resultTemplate(result) {
    const name = this.escape(result.name);
    const sub = result.sub
      ? `<span class="ml-3 font-normal text-gray-500 dark:text-gray-400 truncate">${this.escape(result.sub)}</span>`
      : "";

    return `<li class="flex items-center select-none relative py-2 px-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                data-value="${name}"
                data-action="click->font-candidates--autocomplete#onSelect">
              <span class="font-medium text-gray-900 dark:text-white shrink-0 pointer-events-none">${name}</span>
              <span class="pointer-events-none flex min-w-0">${sub}</span>
            </li>`;
  }

  confirmTemplate(keyword) {
    const text = this.confirmTextValue.replace("{kw}", this.escape(keyword));
    return `<li class="select-none relative py-2 px-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 text-sm text-gray-600 dark:text-gray-300"
                data-action="click->font-candidates--autocomplete#onConfirm">${text}</li>`;
  }

  onSelect = (e) => {
    const value = e.currentTarget.dataset.value;
    if (value != null) this.inputTarget.value = value;
    this.lastKeyword = this.inputTarget.value.trim();
    this.resetActiveItem();
    this.hide();
  };

  onConfirm = () => {
    // 입력한 텍스트 그대로 사용. 드롭다운만 닫는다.
    this.resetActiveItem();
    this.hide();
  };

  // ===== 키보드 위/아래 탐색 =====
  resetActiveItem = () => {
    this.activeIndex = -1;
  };

  navigableItems = () => {
    return Array.from(this.resultsTarget.querySelectorAll("li[data-action]"));
  };

  highlightActiveItem = (items) => {
    items.forEach((el, index) => {
      const active = index === this.activeIndex;
      el.classList.toggle("bg-gray-100", active);
      el.classList.toggle("dark:bg-gray-800", active);
      if (active) el.scrollIntoView({ block: "nearest" });
    });
  };

  moveActiveItem = (delta) => {
    const items = this.navigableItems();
    if (items.length === 0) return;
    let next = this.activeIndex + delta;
    if (next < 0) next = items.length - 1;
    else if (next >= items.length) next = 0;
    this.activeIndex = next;
    this.highlightActiveItem(items);
  };

  onKeydown = (e) => {
    const isHidden = this.resultsTarget.classList.contains("hidden");
    switch (e.key) {
      case "ArrowDown":
        if (isHidden) return;
        e.preventDefault();
        this.moveActiveItem(1);
        break;
      case "ArrowUp":
        if (isHidden) return;
        e.preventDefault();
        this.moveActiveItem(-1);
        break;
      case "Enter": {
        const active = this.navigableItems()[this.activeIndex];
        if (active) {
          // 하이라이트된 항목에서 엔터 = 클릭과 동일.
          e.preventDefault();
          active.click();
        }
        break;
      }
      case "Escape":
        this.resetActiveItem();
        this.hide();
        break;
    }
  };

  show = () => this.resultsTarget.classList.remove("hidden");
  hide = () => this.resultsTarget.classList.add("hidden");

  onClickOutside(e) {
    if (!this.element.contains(e.target)) this.hide();
  }

  escape(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
